import { useTranslation } from "react-i18next";
import {
  mcqFamilyPrompt,
  type MCQOption,
  type MultipleChoiceContent,
} from "../content-types";
import { useMemo } from "react";
import { Explanation, Markup, OptionRow, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import { stableShuffle } from "./mcq-shuffle";
import type { QuestionRendererProps } from "./types";

const LETTERS = "ABCDEFGH";

/** Renders multiple_choice (single or multi-correct) and the single-select
 *  MCQ-family types (scenario_application, analogy, cause_effect, …). */
export function McqRenderer({
  type,
  itemId,
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as MultipleChoiceContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  // Deterministically shuffle the options to neutralise the LLM's positional
  // bias (correct answer "always A"). Grading keys on `option.id`, so reordering
  // the display is safe; we remap the shown A/B/C/D letter by position below.
  // Mirrors iOS `MCQOptionShuffle`. Seeded by the item id so it's stable across
  // re-renders and re-encounters.
  const rawOptions: MCQOption[] = c.options ?? [];
  const options: MCQOption[] = useMemo(
    () => stableShuffle(rawOptions, itemId || c.question || ""),
    [rawOptions, itemId, c.question],
  );
  const correctIds =
    type === "multiple_choice" && c.correct_option_ids && c.correct_option_ids.length >= 2
      ? c.correct_option_ids
      : [c.correct_option_id];
  const isMulti = correctIds.length >= 2;

  const prompt = type === "multiple_choice" ? c.question : mcqFamilyPrompt(type, content);
  const imageUrl = type === "image_question" ? (content.image_url as string | undefined) : undefined;

  const selected = isMulti ? ((answer as string[]) ?? []) : answer;

  const toggle = (id: string) => {
    if (revealed) return;
    if (isMulti) {
      const set = new Set((answer as string[]) ?? []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      setAnswer([...set]);
    } else {
      setAnswer(id);
    }
  };

  const stateFor = (id: string): "neutral" | "selected" | "correct" | "wrong" => {
    const isSelected = isMulti ? (selected as string[]).includes(id) : selected === id;
    if (!revealed) return isSelected ? "selected" : "neutral";
    if (correctIds.includes(id)) return "correct";
    if (isSelected) return "wrong";
    return "neutral";
  };

  return (
    <div>
      <Prompt>{enableGloss ? <GlossableText text={prompt} context={prompt} /> : prompt}</Prompt>
      {imageUrl && (
        <img src={imageUrl} alt="" className="mt-3 max-h-80 w-full rounded-xl object-contain" />
      )}
      {isMulti && <p className="mt-1 text-sm text-ink-faint">{t("Select all that apply.")}</p>}
      <div className="mt-4 space-y-2">
        {options.map((o, i) => (
          <OptionRow key={o.id} state={stateFor(o.id)} disabled={revealed} onClick={() => toggle(o.id)}>
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-black/5 text-xs font-bold text-ink-soft">
              {LETTERS[i] ?? o.id}
            </span>
            <span className="text-ink">
              {enableAnswerGloss ? (
                <GlossableText text={o.text} context={prompt} />
              ) : (
                <Markup>{o.text}</Markup>
              )}
            </span>
          </OptionRow>
        ))}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
